// Copyright 2025, University of Maryland, All Rights Reserved
import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { buildUrl } from './url-builder';
import { RequestOptions, ApiClientConfig } from './types';

export class ApiClient {
  private instance: AxiosInstance;
  private config: ApiClientConfig;

  constructor(config: ApiClientConfig) {
    this.config = config;

    this.instance = axios.create({
      baseURL: config.baseURL,
      headers: {
        'Accept': 'application/json'
      }
    });

    // Add auth token to all requests
    this.instance.interceptors.request.use((config) => {
      if (this.config.debug) {
        console.log(`[API] Request to ${config.url} (auth: CAS)`);
      }

      // CAS authentication - read from localStorage
      const token = localStorage.getItem('cas_jwt');
      if (token && config.headers) {
        config.headers['Authorization'] = `Bearer ${token}`;
        if (this.config.debug) {
          console.log('[API] Added CAS Bearer token');
        }
      }

      // Add debug header if needed
      const isDebugMode = sessionStorage.getItem('debugModeEnabled') === 'true';
      if (isDebugMode && config.headers) {
        config.headers['X-Debug-Mode'] = 'true';
        if (this.config.debug) {
          console.log('[API] Adding X-Debug-Mode header');
        }
      }

      return config;
    });

    // Global response handling
    this.instance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (axios.isAxiosError(error)) {
          if (this.config.debug) {
            console.error(`[API] Error ${error.response?.status}: ${error.config?.url}`);
          }

          if (error.response?.status === 401 || error.response?.status === 403) {
            if (this.config.debug) {
              console.error(`[API] Authorization error (${error.response.status})`);
            }
            // Dispatch access-denied event to trigger the access denied screen
            document.dispatchEvent(new CustomEvent('access-denied'));
          }
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Make a request with proper URL construction based on request type
   */
  public async request<T>(options: RequestOptions): Promise<AxiosResponse<T>> {
    // Build the appropriate URL based on the request type
    if (options.url) {
      options.url = buildUrl(options.url, options);
    }

    // Log the request for debugging
    if (this.config.debug) {
      console.log(`[API] ${options.method || 'GET'} ${options.url}`);
    }

    return this.instance.request<T>(options);
  }

  /**
   * GET request helper
   */
  public async get<T>(url: string, options: RequestOptions = {}): Promise<AxiosResponse<T>> {
    return this.request<T>({ ...options, url, method: 'GET' });
  }

  /**
   * POST request helper
   */
  public async post<T>(url: string, data?: unknown, options: RequestOptions = {}): Promise<AxiosResponse<T>> {
    return this.request<T>({ ...options, url, data, method: 'POST' });
  }

  /**
   * PUT request helper
   */
  public async put<T>(url: string, data?: unknown, options: RequestOptions = {}): Promise<AxiosResponse<T>> {
    return this.request<T>({ ...options, url, data, method: 'PUT' });
  }

  /**
   * DELETE request helper
   */
  public async delete<T>(url: string, options: RequestOptions = {}): Promise<AxiosResponse<T>> {
    return this.request<T>({ ...options, url, method: 'DELETE' });
  }

  /**
   * Get the underlying axios instance
   */
  public getAxiosInstance(): AxiosInstance {
    return this.instance;
  }
}

/**
 * Factory function to create an API client instance
 */
export function createApiClient(config: ApiClientConfig): ApiClient {
  return new ApiClient(config);
}